import React, { useState, useEffect } from 'react';
import { Table } from '../ui/Table';
import styled from '@emotion/styled';
import { Eye, Car, DollarSign, Calendar, Settings, Palette, Gauge, Zap, X, ChevronLeft, ChevronRight } from 'lucide-react';

const ImageCell = styled.div`
  width: 80px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  img {
    max-width: 100%;
    max-height: 60px;
    border-radius: 8px;
    background: #232325;
    object-fit: cover;
  }
  
  @media (max-width: 1200px) {
    width: 70px;
    height: 50px;
    
    img {
      max-height: 50px;
      border-radius: 6px;
    }
  }
  
  @media (max-width: 768px) {
    width: 60px;
    height: 45px;
    
    img {
      max-height: 45px;
      border-radius: 4px;
    }
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 40px;
    
    img {
      max-height: 40px;
      border-radius: 4px;
    }
  }
`;

const ImageCountBadge = styled.div`
  position: absolute;
  top: -8px;
  left: -8px;
  background: #F43F60;
  color: white;
  font-size: 12px;
  font-weight: 400;
  border-radius: 100%;
  width: 24px;
  height: 24px;
  text-align: center;
  border: 2px solid #232325;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    font-size: 9px;
    top: -6px;
    left: -6px;
  }
  
  @media (max-width: 480px) {
    width: 18px;
    height: 18px;
    font-size: 8px;
    top: -5px;
    left: -5px;
  }
`;

const VehicleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  @media (max-width: 768px) {
    gap: 2px;
  }
`;

const VehicleTitle = styled.div`
  color: #ededed;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.2;
  
  @media (max-width: 1200px) {
    font-size: 15px;
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
  
`;

const VehicleSubtitle = styled.div`
  color: #9E9E9E;
  font-size: 14px;
  line-height: 1.2;

`;

const PriceBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(34, 197, 94, 0.2);
  color: #22C55E;
  font-weight: 600;
  font-size: 16px;
  border-radius: 8px;
  padding: 4px 8px;
  min-width: 80px;
  justify-content: center;
  
  @media (max-width: 1200px) {
    font-size: 15px;
    min-width: 70px;
    padding: 3px 6px;
  }
  
  @media (max-width: 768px) {
    font-size: 15px;
    min-width: 60px;
    padding: 2px 4px;
    gap: 2px;
  }
  
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
    font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
        switch (props.status) {
            case 'in_stock': return 'rgba(34, 197, 94, 0.2)';
            case 'sold': return 'rgba(239, 68, 68, 0.2)';
            case 'pending': return 'rgba(245, 158, 11, 0.2)';
            case 'reserved': return 'rgba(59, 130, 246, 0.2)';
            default: return 'rgba(107, 114, 128, 0.2)';
        }
    }};
  color: ${props => {
        switch (props.status) {
            case 'in_stock': return '#22C55E';
            case 'sold': return '#EF4444';
            case 'pending': return '#F59E0B';
            case 'reserved': return '#3B82F6';
            default: return '#6B7280';
        }
    }};
  
  @media (max-width: 1200px) {
    padding: 3px 10px;
  }
  
  @media (max-width: 768px) {
    padding: 2px 8px;
  }
  
  @media (max-width: 480px) {
    padding: 2px 6px;
  }
`;

const AccentLink = styled.a`
  color: #F43F60;
  text-decoration: none;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

const MileageBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(107, 114, 128, 0.2);
  color: #9E9E9E;
  font-weight: 500;
  font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  
  @media (max-width: 1200px) {
    font-size: 11px;
    padding: 2px 5px;
  }
  
  @media (max-width: 768px) {
    padding: 1px 4px;
    gap: 2px;
  }
  

`;

const CertificationBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(139, 92, 246, 0.2);
  color: #8B5CF6;
  font-weight: 600;
  font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  text-transform: uppercase;
  
  @media (max-width: 1200px) {
    padding: 2px 5px;
  }
  
  @media (max-width: 768px) {
    padding: 1px 4px;
    gap: 2px;
  }

`;

const EngineBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(59, 130, 246, 0.2);
  color: #3B82F6;
  font-weight: 500;
  font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  
  @media (max-width: 1200px) {
    padding: 2px 5px;
  }
  
  @media (max-width: 768px) {
    padding: 1px 4px;
    gap: 2px;
  }
  
`;

const TransmissionBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(245, 158, 11, 0.2);
  color: #F59E0B;
  font-weight: 500;
   font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  
  @media (max-width: 1200px) {
    padding: 2px 5px;
  }
  
  @media (max-width: 768px) {
    padding: 1px 4px;
    gap: 2px;
  }
  
`;

const ColorBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(236, 72, 153, 0.2);
  color: #EC4899;
  font-weight: 500;
  font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  
  @media (max-width: 1200px) {
    padding: 2px 5px;
  }
  
  @media (max-width: 768px) {
    padding: 1px 4px;
    gap: 2px;
  }
  
`;

const MPGBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(34, 197, 94, 0.2);
  color: #22C55E;
  font-weight: 500;
  font-size: 16px;
  border-radius: 6px;
  padding: 2px 6px;
  
  @media (max-width: 1200px) {
    padding: 2px 5px;
  }
  
  @media (max-width: 768px) {
    padding: 1px 4px;
    gap: 2px;
  }

`;

const ImageCarouselModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(37, 37, 60, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 5px;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: 95vh;
  }
  
  @media (max-width: 480px) {
    max-width: 98vw;
    max-height: 98vh;
  }
`;

const CarouselImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  
  @media (max-width: 768px) {
    max-height: 95vh;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    max-height: 98vh;
    border-radius: 4px;
  }
`;

const CarouselControls = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
  align-items: center;
  
  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    top: 5px;
    right: 5px;
    gap: 5px;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 4px;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 12px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
  }
`;

const PrevButton = styled(NavigationButton)`
  left: 20px;
  
  @media (max-width: 768px) {
    left: 10px;
  }
  
  @media (max-width: 480px) {
    left: 5px;
  }
`;

const NextButton = styled(NavigationButton)`
  right: 20px;
  
  @media (max-width: 768px) {
    right: 10px;
  }
  
  @media (max-width: 480px) {
    right: 5px;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    bottom: 10px;
    padding: 6px 12px;
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    bottom: 5px;
    padding: 4px 8px;
    font-size: 10px;
  }
`;

const CarouselVehicleTitle = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  max-width: 400px;
  
  @media (max-width: 768px) {
    top: 10px;
    left: 10px;
    padding: 8px 12px;
    font-size: 14px;
    max-width: 300px;
  }
  
  @media (max-width: 480px) {
    top: 5px;
    left: 5px;
    padding: 6px 8px;
    font-size: 12px;
    max-width: 250px;
  }
`;

const ClickableRow = styled.div`
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(244, 63, 96, 0.1);
  }
`;

function formatDate(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatMileage(mileage) {
    if (!mileage) return '--';
    return mileage?.toLocaleString() + ' mi';
}

function formatPrice(price) {
    if (!price) return '--';
    return '$' + price?.toLocaleString();
}

function formatMPG(city, highway) {
    if (!city && !highway) return '--';
    if (city && highway) return `${city}/${highway}`;
    return city || highway;
}

const columns = [
    {
        key: 'number',
        label: 'No.',
        render: (_item, idx) => <span style={{ fontWeight: 700 }}>{String(idx + 1).padStart(2, '0')}</span>,
    },
    {
        key: 'image',
        label: 'Image',
        render: (vehicle) => {
            // Parse images from imagelist string
            let images = [];
            try {
                if (vehicle.imagelist && typeof vehicle.imagelist === 'string') {
                    images = JSON.parse(vehicle.imagelist);
                }
            } catch (e) {
                console.error('Error parsing imagelist:', e);
            }
            
            const imageCount = images?.length || 0;
            return (
                <ImageCell>
                    <img
                        src={images?.[0]?.url || '/car-placeholder.png'}
                        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                        onError={(e) => {
                            e.target.src = '/car-placeholder.png';
                        }}
                    />
                    {imageCount > 1 && (
                        <ImageCountBadge>
                            {imageCount}
                        </ImageCountBadge>
                    )}
                </ImageCell>
            );
        },
    },
    {
        key: 'vehicle',
        label: 'Vehicle',
        render: (vehicle) => (
            <VehicleInfo>
                <VehicleTitle>
                    {vehicle.year} {vehicle.make} {vehicle.model}
                </VehicleTitle>
                <VehicleSubtitle>
                    {vehicle.trim}
                </VehicleSubtitle>
                <VehicleSubtitle>
                    Stock: {vehicle.stock_no} • VIN: {vehicle.vin}
                </VehicleSubtitle>
            </VehicleInfo>
        ),
    },
    {
        key: 'price',
        label: 'Price',
        render: (vehicle) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <PriceBadge>
                    {formatPrice(vehicle.price)}
                </PriceBadge>
                {vehicle.msrp && vehicle.msrp !== vehicle.price && (
                    <div style={{ fontSize: '14px', color: '#9E9E9E' }}>
                        MSRP: {formatPrice(vehicle.msrp)}
                    </div>
                )}
            </div>
        ),
    },
    {
        key: 'mileage',
        label: 'Mileage',
        render: (vehicle) => (
            <MileageBadge>
                <Car size={14} />
                {formatMileage(vehicle.mileage)}
            </MileageBadge>
        ),
    },
    {
        key: 'status',
        label: 'Status',
        render: (vehicle) => {
            const status = vehicle.status || 'Live';
            return <StatusBadge status={status}>{status}</StatusBadge>;
        },
    },
    {
        key: 'engine',
        label: 'Engine',
        render: (vehicle) => (
            <EngineBadge>
                <Settings size={14} />
                {vehicle.engine || '--'}
            </EngineBadge>
        ),
    },
    {
        key: 'transmission',
        label: 'Transmission',
        render: (vehicle) => (
            <TransmissionBadge>
                <Gauge size={14} />
                {vehicle.transmission || '--'}
            </TransmissionBadge>
        ),
    },
    {
        key: 'driveTrain',
        label: 'Drive Train',
        render: (vehicle) => (
            <div style={{ color: '#ededed', fontSize: '16px' }}>
                {vehicle.drivetrain || '--'}
            </div>
        ),
    },
    {
        key: 'colors',
        label: 'Colors',
        render: (vehicle) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {vehicle.ext_color && (
                    <ColorBadge>
                        <Palette size={14} />
                        Ext: {vehicle.ext_color}
                    </ColorBadge>
                )}
                {vehicle.int_color && (
                    <ColorBadge>
                        <Palette size={14} />
                        Int: {vehicle.int_color}
                    </ColorBadge>
                )}
                {!vehicle.ext_color && !vehicle.int_color && (
                    <span style={{ color: '#9E9E9E', fontSize: '16px' }}>--</span>
                )}
            </div>
        ),
    },
    {
        key: 'mpg',
        label: 'MPG',
        render: (vehicle) => (
            <MPGBadge>
                <Zap size={14} />
                {formatMPG(vehicle.mpg_city, vehicle.mpg)}
            </MPGBadge>
        ),
    },
    {
        key: 'dateInStock',
        label: 'Date In Stock',
        render: (vehicle) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Calendar size={14} />
                {formatDate(vehicle.date_in_stock)}
            </div>
        ),
    },
    {
        key: 'certification',
        label: 'Certification',
        render: (vehicle) => {
            if (vehicle.certified === 'certified') {
                return <CertificationBadge>Certified</CertificationBadge>;
            }
            return <span style={{ color: '#9E9E9E', fontSize: '16px' }}>--</span>;
        },
    },
    {
        key: 'condition',
        label: 'Condition',
        render: (vehicle) => {
            const condition = vehicle.new_used || 'Used';
            return <span style={{ color: '#ededed', fontWeight: '500', fontSize: '16px', }}>{condition}</span>;
        },
    },
    {
        key: 'bodyStyle',
        label: 'Body Style',
        render: (vehicle) => (
            <div style={{ color: '#ededed', fontSize: '16px' }}>
                {vehicle.body_style || '--'}
            </div>
        ),
    },
    {
        key: 'doors',
        label: 'Doors',
        render: (vehicle) => (
            <div style={{ color: '#ededed', fontSize: '16px' }}>
                {vehicle.doors || '--'}
            </div>
        ),
    },
    {
        key: 'cylinders',
        label: 'Cylinders',
        render: (vehicle) => (
            <div style={{ color: '#ededed', fontSize: '16px' }}>
                {vehicle.cylinders || '--'}
            </div>
        ),
    },
    {
        key: 'fuel',
        label: 'Fuel',
        render: (vehicle) => (
            <div style={{ color: '#ededed', fontSize: '16px' }}>
                {vehicle.fuel || '--'}
            </div>
        ),
    },
    {
        key: 'specialPrice',
        label: 'Special Price',
        render: (vehicle) => (
            <div style={{ color: '#ededed', fontSize: '16px' }}>
                {vehicle.special_price && vehicle.special_price !== '0' ? 
                    formatPrice(vehicle.special_price) : '--'}
            </div>
        ),
    },
    {
        key: 'websiteStatus',
        label: 'Website Status',
        render: (vehicle) => {
            const status = vehicle.website_status || '--';
            return (
                <div style={{ 
                    color: status === 'active' ? '#10B981' : '#F59E0B', 
                    fontSize: '16px',
                    fontWeight: '500',
                    textTransform: 'capitalize'
                }}>
                    {status}
                </div>
            );
        },
    },
    {
        key: 'vdpClicks',
        label: 'VDP Clicks',
        render: (vehicle) => (
            <div style={{ color: '#ededed', fontSize: '16px' }}>
                {vehicle.vdp_clicks || 0}
            </div>
        ),
    },
    {
        key: 'source',
        label: 'Source',
        render: (vehicle) => (
            <div style={{ color: '#ededed', fontSize: '16px', textTransform: 'capitalize' }}>
                {vehicle.source || '--'}
            </div>
        ),
    },
    {
        key: 'dealerInfo',
        label: 'Dealer Info',
        render: (vehicle) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ color: '#ededed', fontSize: '16px', textTransform: 'capitalize' }}>
                    {vehicle.first_name} {vehicle.last_name}
                </div>
                <div style={{ color: '#9E9E9E', fontSize: '14px' }}>
                    {vehicle.dealer_phone_number}
                </div>
                <div style={{ color: '#9E9E9E', fontSize: '14px' }}>
                    {vehicle.city}
                </div>
            </div>
        ),
    },
    {
        key: 'comment',
        label: 'Description',
        render: (vehicle) => (
            <div style={{
                color: '#9E9E9E',
                fontSize: '16px',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            }}>
                {vehicle.description || '--'}
            </div>
        ),
    },
    {
        key: 'views',
        label: 'Views',
        render: (vehicle) => (
            <div style={{ color: '#ededed', fontSize: '16px' }}>
                {vehicle.website_views || 0} times
            </div>
        ),
    },
    {
        key: 'created',
        label: 'Created',
        render: (vehicle) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '16px' }}>
                <Calendar size={12} />
                {vehicle.created ? formatDate(vehicle.created) : '--'}
            </div>
        ),
    },
    {
        key: 'updated',
        label: 'Updated',
        render: (vehicle) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '16px' }}>
                <Calendar size={12} />
                {vehicle.updated ? formatDate(vehicle.updated) : '--'}
            </div>
        ),
    },
    {
        key: 'showInSpecials',
        label: 'Show in Specials',
        render: (vehicle) => (
            <div style={{ 
                color: vehicle.show_in_specials === 'yes' ? '#10B981' : '#9E9E9E', 
                fontSize: '16px',
                fontWeight: '500'
            }}>
                {vehicle.show_in_specials === 'yes' ? 'Yes' : 'No'}
            </div>
        ),
    },
    {
        key: 'onHold',
        label: 'On Hold',
        render: (vehicle) => (
            <div style={{ 
                color: vehicle.on_hold === 'yes' ? '#F59E0B' : '#9E9E9E', 
                fontSize: '16px',
                fontWeight: '500'
            }}>
                {vehicle.on_hold === 'yes' ? 'Yes' : 'No'}
            </div>
        ),
    },
];

const ImageCarousel = ({ vehicle, isOpen, onClose }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const handlePrevious = () => {
        setCurrentImageIndex(prev =>
            prev === 0 ? images.length - 1 : prev - 1
        );
    };

    const handleNext = () => {
        setCurrentImageIndex(prev =>
            prev === images.length - 1 ? 0 : prev + 1
        );
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onClose();
        } else if (e.key === 'ArrowLeft') {
            handlePrevious();
        } else if (e.key === 'ArrowRight') {
            handleNext();
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // Parse images from imagelist string
    let images = [];
    try {
        if (vehicle?.imagelist && typeof vehicle.imagelist === 'string') {
            images = JSON.parse(vehicle.imagelist);
        }
    } catch (e) {
        console.error('Error parsing imagelist:', e);
    }

    if (!isOpen || !vehicle || !images || images.length === 0) {
        return null;
    }

    return (
        <ImageCarouselModal onClick={onClose}>
            <CarouselContainer onClick={(e) => e.stopPropagation()}>
                <CarouselVehicleTitle>
                    {vehicle.year} {vehicle.make} {vehicle.model} - {vehicle.trim}
                </CarouselVehicleTitle>

                <CarouselControls>
                    <CloseButton onClick={onClose}>
                        <X size={20} />
                    </CloseButton>
                </CarouselControls>

                <CarouselImage
                    src={images[currentImageIndex].url}
                    alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} - Image ${currentImageIndex + 1}`}
                    onError={(e) => {
                        e.target.src = '/car-placeholder.png';
                    }}
                />

                <PrevButton onClick={handlePrevious} disabled={images.length <= 1}>
                    <ChevronLeft size={24} />
                </PrevButton>

                <NextButton onClick={handleNext} disabled={images.length <= 1}>
                    <ChevronRight size={24} />
                </NextButton>

                <ImageCounter>
                    {currentImageIndex + 1} of {images.length}
                </ImageCounter>
            </CarouselContainer>
        </ImageCarouselModal>
    );
};

const InventoryTable = ({ vehicles, height }) => {
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [isCarouselOpen, setIsCarouselOpen] = useState(false);

    const handleRowClick = (vehicle) => {
        // Parse images from imagelist string
        let images = [];
        try {
            if (vehicle.imagelist && typeof vehicle.imagelist === 'string') {
                images = JSON.parse(vehicle.imagelist);
            }
        } catch (e) {
            console.error('Error parsing imagelist:', e);
        }
        
        if (images && images.length > 0) {
            setSelectedVehicle(vehicle);
            setIsCarouselOpen(true);
        }
    };

    const handleCloseCarousel = () => {
        setIsCarouselOpen(false);
        setSelectedVehicle(null);
    };

    // Wrap each row with clickable functionality
    const enhancedData = vehicles.map(vehicle => {
        // Parse images from imagelist string
        let images = [];
        try {
            if (vehicle.imagelist && typeof vehicle.imagelist === 'string') {
                images = JSON.parse(vehicle.imagelist);
            }
        } catch (e) {
            console.error('Error parsing imagelist:', e);
        }
        
        return {
            ...vehicle,
            _rowProps: {
                onClick: () => handleRowClick(vehicle),
                style: { cursor: images && images.length > 0 ? 'pointer' : 'default' }
            }
        };
    });

    return (
        <>
            <Table columns={columns} data={enhancedData} height={height} />
            <ImageCarousel
                vehicle={selectedVehicle}
                isOpen={isCarouselOpen}
                onClose={handleCloseCarousel}
            />
        </>
    );
};

export default InventoryTable; 